import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { SHOPIFY_PRODUCT_URL } from '@/shopify';
import { fetchShopifyProduct, startShopifyCheckout, formatPrice } from '@/lib/shopifyClient';
import { HOME } from '@/constants/testIds';

const SLOTS = [
    {
        id: HOME.shopifyCheckoutSlot,
        slot: 'checkout',
        label: 'Shopify checkout',
        body: (connected) =>
            connected
                ? 'Connected via the Storefront API. Payment opens on your Shopify-hosted checkout.'
                : 'Connects here. Payment opens on Shopify\u2019s hosted checkout \u2014 no custom payment system.',
    },
    {
        id: HOME.shopifyDeliverySlot,
        slot: 'digital-download',
        label: 'Digital download delivery',
        body: () => 'Connects here. Delivery is handled by Shopify after checkout \u2014 no custom payment system.',
    },
];

export const Purchase = () => {
    const [store, setStore] = useState({ configured: false, product: null });
    const [opening, setOpening] = useState(false);

    useEffect(() => {
        fetchShopifyProduct()
            .then((data) => setStore({ configured: !!data.configured, product: data.product }))
            .catch(() => setStore({ configured: false, product: null }));
    }, []);

    const connected = store.configured && !!store.product;
    const price = connected ? formatPrice(store.product.price) || '$4.99' : '$4.99';

    const buy = async (event) => {
        event.preventDefault();
        if (!store.configured) {
            window.location.href = SHOPIFY_PRODUCT_URL;
            return;
        }
        setOpening(true);
        try {
            const data = await startShopifyCheckout();
            window.location.href = data.checkoutUrl || SHOPIFY_PRODUCT_URL;
        } catch {
            window.location.href = SHOPIFY_PRODUCT_URL;
        } finally {
            setOpening(false);
        }
    };

    return (
        <section id="get-engine" data-testid={HOME.purchase} className="relative py-24 md:py-32">
            <div className="mx-auto max-w-3xl px-6 text-center">
                <Reveal>
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-aqua">Pricing</p>
                    <h2 className="mt-4 text-3xl font-light tracking-tight sm:text-4xl lg:text-5xl">Get ENGINE.</h2>
                    <p className="mt-6 text-5xl font-light tracking-tight text-white md:text-6xl">
                        {price}
                        <span className="ml-3 align-middle font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                            one-time · no subscription
                        </span>
                    </p>
                </Reveal>

                <Reveal delay={0.1}>
                    <a
                        data-testid={HOME.buyButton}
                        href={SHOPIFY_PRODUCT_URL}
                        onClick={buy}
                        className="group mt-10 inline-flex h-12 items-center gap-2 rounded-full bg-aqua px-8 text-sm font-medium text-ink transition-all duration-300 hover:shadow-glow hover:brightness-110"
                    >
                        {opening ? 'Opening checkout…' : `Get ENGINE — ${price}`}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </a>
                    <p
                        data-testid="shopify-status-label"
                        className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600"
                    >
                        {store.configured
                            ? `Shopify connected — live checkout at ${store.product?.handle || 'engine'}`
                            : 'Awaiting store domain — activates the moment it is added'}
                    </p>
                </Reveal>

                <Reveal delay={0.18}>
                    <div className="mt-14 grid grid-cols-1 gap-4 text-left sm:grid-cols-2">
                        {SLOTS.map((s) => (
                            <div
                                key={s.slot}
                                data-testid={s.id}
                                data-shopify-slot={s.slot}
                                className="rounded-2xl border border-dashed border-white/20 bg-surface-1/50 p-6"
                            >
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-aqua">{s.label}</p>
                                <p className="mt-2 text-xs leading-relaxed text-zinc-500">{s.body(connected)}</p>
                            </div>
                        ))}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
